(function () {
  /**
   * @deprecated Please use src2/core/services/fulfillmentEvent/FulfillmentEventList.js
   *
   * @ngdoc factory
   * @name FulfillmentEventList
   * @description Model for a fulfillment event list
   */
  angular
    .module('binky.core.fulfillment-events')
    .factory('FulfillmentEventList', getFulfillmentEventListModel);

  /* @ngInject */
  function getFulfillmentEventListModel(
    $q,
    $rootScope,
    _,
    binkySrc2,
    feature,
    FULFILLMENT_EVENT_LIST_CACHE_ID,
    FULFILLMENT_EVENT_LIST_CONTEXT,
    FULFILLMENT_EVENT_LIST_EVENT,
    FULFILLMENT_EVENT_STATUS,
    FulfillmentEvent,
    IN_PROGRESS_FULFILLMENT_EVENT_LIST_CACHE_ID,
    IN_PROGRESS_FULFILLMENT_EVENT_LIST_EVENT,
    jilFulfillmentEvents,
    LICENSE_FULFILLMENT_EVENT_LIST_CACHE_ID,
    LICENSE_FULFILLMENT_EVENT_LIST_EVENT,
    List,
    modelCache,
    promiseUtils
  ) {
    class FulfillmentEventList extends List {
      /**
       * @description Method to create a new instance of a FulfillmentEventList.
       *
       * @param {Object} options - configures the FulfillmentEventList model
       *  instance.
       * @param {Date} [options.endDate] - Upper bound to filter fulfillment
       *  events based on date.
       * @param {FULFILLMENT_EVENT_LIST_CONTEXT} [options.listContext] - String that indicates what
       *  context this list is being used. One of IN_PROGRESS, LICENSE, ORGANIZATION (default).
       * @param {string} options.orgId - Organization id that this list's
       *  fulfillment events belong to.
       * @param {string} [options.productId] - Product id that will be used to fetch fulfillment
       *  events for a license.
       * @param {Date} [options.startDate] - Lower bound to filter fulfillment
       *  events based on date.
       * @param {string} [options.status] - Status value to filter by fulfillment event status.
       */
      constructor(options = {}) {
        const listContext = _.get(
          options,
          'listContext',
          FULFILLMENT_EVENT_LIST_CONTEXT.ORGANIZATION
        );
        super({
          cacheType: getCacheId(listContext),
          itemClassRef: FulfillmentEvent,
          resource:
            listContext === FULFILLMENT_EVENT_LIST_CONTEXT.LICENSE
              ? jilFulfillmentEvents.licenseFulfillmentEvents
              : jilFulfillmentEvents.fulfillmentEvents,
        });

        this.$resolved = false;

        _.assign(this, {
          endDate: options.endDate,
          listContext,
          orgId: options.orgId,
          productId: options.productId,
          startDate: options.startDate,
          status: options.status,
          useCache: _.get(options, 'useCache', true),
        });
      }

      /**
       * @description Gets a list of contract ids that have fulfillment events.
       *  NOTE: This should only be used by the in progress fulfillment event
       *  list used by the fulfillmentEventRefreshManager.
       * @returns {string[]} The contract ids who have fulfillment events.
       */
      getContractsWithFulfillmentEvents() {
        return _(this.items)
          .flatMap((item) => _.flatMap(item.offers, 'contractId'))
          .uniq()
          .value();
      }

      /**
       * @description Gets a list of offer ids that have fulfillment events.
       *  NOTE: This should only be used by the in progress fulfillment event
       *  list used by the fulfillmentEventRefreshManager.
       * @returns {string[]} The offer ids who have corresponding
       *  fulfillment events.
       */
      getOffersWithFulfillmentEvents() {
        return _(this.items)
          .flatMap((item) => _.flatMap(item.offers, 'offerId'))
          .uniq()
          .value();
      }

      /**
       * @description Helper method to check if there is at least one
       *  fulfillment event with in progress status to assist the refreshing
       *  mechanism.
       *
       * @returns {boolean} True if there is an IN_PROGRESS fulfillment event,
       *  false otherwise.
       */
      hasInProgressFulfillmentEvents() {
        return _.some(this.items, ['status', FULFILLMENT_EVENT_STATUS.IN_PROGRESS]);
      }

      /**
       * @description Overidden key method to be used when we fetch models from
       *  modelCache.
       *
       * @returns {string} key formed from endDate, startDate, status, orgId,
       *  and the productId.
       */
      key() {
        return super.key(getParams(this));
      }

      /**
       * @description Method to refresh the fulfillment event list from the backend.
       *
       * @returns {Promise} resolves to FulfillmentEventList on success, else
       *  rejects with error.
       */
      refresh() {
        const refreshParams = getParams(this);
        return super.refresh(refreshParams).then((_fulfillmentEventList) => {
          $rootScope.$emit(getEventConstant(this.listContext).UPDATE, this);
          if (this.shouldUpdateTotalItemCount(refreshParams)) {
            $rootScope.$emit(
              getEventConstant(this.listContext).UPDATE_COUNT,
              this.pagination.itemCount
            );
          }
          return _fulfillmentEventList;
        });
      }

      /**
       * @description Helper method to call the parent class
       *  shouldUpdateTotalItemCount, combined with conditions to make the
       *  result false if filters were set.
       *
       * @param {Object} params - top level wrapper object.
       * @param {Date} [params.end_date] - Upper bound to filter fulfillment
       *  events based on date. Note this is underscored because we're using the getParams() output.
       * @param {Date} [params.start_date] - Lower bound to filter fulfillment
       *  events based on date. Note this is underscored because we're using the getParams() output.
       * @param {string} [params.status] - Status value to filter by fulfillment
       *  event status.
       * @returns {boolean} True if an event about the updated count should be
       *  emitted, false otherwise.
       */
      shouldUpdateTotalItemCount(params) {
        return (
          super.shouldUpdateTotalItemCount() &&
          (this.listContext === FULFILLMENT_EVENT_LIST_CONTEXT.ORGANIZATION ||
            this.listContext === FULFILLMENT_EVENT_LIST_CONTEXT.LICENSE) &&
          _.isNil(params.end_date) &&
          _.isNil(params.start_date) &&
          _.isNil(params.status)
        );
      }

      /**
       * @description Method to fetch the FulfillmentEventList.
       * @param {Object} options - top level wrapper object.
       * @param {FULFILLMENT_EVENT_LIST_CONTEXT} options.listContext - String specifying what
       *  context this list is being used in. One of: IN_PROGRESS, LICENSE, or ORGANIZATION (default).
       * @param {Date} options.endDate - upper bound date to filter events on.
       * @param {string} options.orgId - Organization id that this list's
       *  fulfillment events belong to.
       * @param {Date} options.startDate - lower bound date to filter events on.
       * @param {string} options.status - status to filter the fulfillment event
       *  list on.
       * @returns {FulfillmentEventList} the fetched FulfillmentEventList.
       */
      static get(options) {
        let model = new FulfillmentEventList(options);

        const cachedModel = modelCache.get(FULFILLMENT_EVENT_LIST_CACHE_ID, model.key());
        if (model.useCache && cachedModel) {
          model = cachedModel;
        } else {
          model.refresh();
        }
        return model;
      }
    }

    class FulfillmentEventList2 extends binkySrc2.services.fulfillmentEvent.FulfillmentEventList {
      /**
       * @description Method to refresh fulfillment event list from back-end.
       * @returns {Promise} resolves to FulfillmentEventList on success, else rejects with error
       */
      refresh() {
        this.$resolved = false;
        this.$promise = $q((resolve, reject) => {
          super
            .refresh()
            .then(() => {
              resolve(this);
            })
            .catch((error) => reject(error.response))
            .finally(() => {
              this.$resolved = true;
            });
        });
        return this.$promise;
      }

      /**
       * @description Method to fetch the FulfillmentEventList.
       * @param {Object} options - options to configure the fulfillment event list.
       * @returns {FulfillmentEventList} the fetched FulfillmentEventList.
       */
      static get(options) {
        const model = new FulfillmentEventList2(options);
        if (feature.isEnabled('temp_src2_cache')) {
          const promise = super.get(options);
          model.$resolved = false;
          model.$promise = promiseUtils
            .toAngularPromise(promise)
            .then((modelList) => {
              _.assign(model, modelList);
              return model;
            })
            .finally(() => {
              model.$resolved = true;
            });
          return model;
        }
        model.refresh();
        return model;
      }
    }

    //////////////

    function getCacheId(context) {
      switch (context) {
        case FULFILLMENT_EVENT_LIST_CONTEXT.IN_PROGRESS:
          return IN_PROGRESS_FULFILLMENT_EVENT_LIST_CACHE_ID;
        case FULFILLMENT_EVENT_LIST_CONTEXT.LICENSE:
          return LICENSE_FULFILLMENT_EVENT_LIST_CACHE_ID;
        default:
          return FULFILLMENT_EVENT_LIST_CACHE_ID;
      }
    }

    function getEventConstant(listContext) {
      switch (listContext) {
        case FULFILLMENT_EVENT_LIST_CONTEXT.IN_PROGRESS:
          return IN_PROGRESS_FULFILLMENT_EVENT_LIST_EVENT;
        case FULFILLMENT_EVENT_LIST_CONTEXT.LICENSE:
          return LICENSE_FULFILLMENT_EVENT_LIST_EVENT;
        default:
          return FULFILLMENT_EVENT_LIST_EVENT;
      }
    }

    function getParams(list) {
      return _.omitBy(
        {
          end_date: list.endDate,
          orgId: list.orgId,
          productId: list.productId,
          start_date: list.startDate,
          status: list.status,
        },
        _.isUndefined
      );
    }

    // eslint-disable-next-line angular/on-watch
    $rootScope.$on(IN_PROGRESS_FULFILLMENT_EVENT_LIST_EVENT.UPDATE, () => {
      if (feature.isEnabled('temp_src2_cache')) {
        binkySrc2.models.cache.modelCache.clear(IN_PROGRESS_FULFILLMENT_EVENT_LIST_CACHE_ID);
      }
      modelCache.removeAll(IN_PROGRESS_FULFILLMENT_EVENT_LIST_CACHE_ID);
    });

    modelCache.register(FULFILLMENT_EVENT_LIST_CACHE_ID, 1);
    modelCache.register(IN_PROGRESS_FULFILLMENT_EVENT_LIST_CACHE_ID, 1);
    modelCache.register(LICENSE_FULFILLMENT_EVENT_LIST_CACHE_ID, 1);

    return feature.isEnabled('temp_react_port_fulfillment_event')
      ? FulfillmentEventList2
      : FulfillmentEventList;
  }
})();
